import { useCallback, useEffect, useState } from "react";

import { styled } from "@linaria/react";
import { CheckIcon, CopyIcon } from "@phosphor-icons/react";

import Button, { ButtonSize, ButtonVariant } from "@gx/dls/buttons/Button";
import Chip, { ChipSize, ChipVariant } from "@gx/dls/chips/Chip";
import FlexWrapper, {
  AlignItems,
  FlexDirection,
  JustifyContent,
} from "@gx/dls/containers/FlexWrapper";
import CodeEditor from "@gx/dls/editor/CodeEditor";
import CopyInput from "@gx/dls/inputs/CopyInput";
import Text, { TextVariant, TextWeight } from "@gx/dls/text/Text";
import { withTheme, type PropsWithTheme } from "@gx/dls/theme";
import Widget from "@gx/dls/widget/Widget";

import { DEFAULT_SIGNATURE_HEADER } from "@/pages/sources/components/create/sources/webhook/constants";
import { type WebhookCredentials } from "@/pages/sources/components/create/sources/webhook/types";
import {
  computeHmacSignature,
  generateCurlCommand,
} from "@/pages/sources/components/create/sources/webhook/utils";

const DEFAULT_TEST_PAYLOAD = '{"test": "data"}';

const StepRow = withTheme(styled.div<PropsWithTheme>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`);

const StepNumber = withTheme(styled.div<PropsWithTheme>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 0.5px solid ${({ theme }) => theme.color.border.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
`);

interface WebhookQuickCurlProps {
  credentials: WebhookCredentials;
  signatureHeader?: string;
}

const WebhookQuickCurl = ({
  credentials,
  signatureHeader = DEFAULT_SIGNATURE_HEADER,
}: WebhookQuickCurlProps) => {
  const [signature, setSignature] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    void computeHmacSignature(credentials.webhookSecret, DEFAULT_TEST_PAYLOAD).then(setSignature);
  }, [credentials.webhookSecret]);

  const testUrl = credentials.webhookUrl.replace("/v1/", "/v1/test/");

  const curlCommand = generateCurlCommand(
    testUrl,
    signatureHeader,
    signature || undefined,
    DEFAULT_TEST_PAYLOAD,
  );

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(curlCommand).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [curlCommand]);

  return (
    <FlexWrapper direction={FlexDirection.COLUMN} gap={12} fillWidth>
      <CopyInput
        label="URL"
        labelTooltip="The URL to send webhook events to."
        value={credentials.webhookUrl}
        fillWidth
      />
      <FlexWrapper direction={FlexDirection.COLUMN} gap={4} fillWidth>
        <FlexWrapper
          alignItems={AlignItems.CENTER}
          justifyContent={JustifyContent.SPACE_BETWEEN}
          fillWidth
        >
          <Text variant={TextVariant.SECONDARY}>Signature Key</Text>
          <Chip
            label="Shown once — save this now"
            variant={ChipVariant.ERROR}
            size={ChipSize.SMALL}
          />
        </FlexWrapper>
        <CopyInput
          labelTooltip="The HMAC key used to sign requests. You will not be able to see this again."
          value={credentials.webhookSecret}
          fillWidth
        />
      </FlexWrapper>

      <Widget fillWidth noHover>
        <FlexWrapper direction={FlexDirection.COLUMN} gap={10} fillWidth>
          <Text variant={TextVariant.TERTIARY} weight={TextWeight.MEDIUM}>
            How to send a test
          </Text>
          <StepRow>
            <StepNumber>
              <Text variant={TextVariant.TERTIARY}>1</Text>
            </StepNumber>
            <Text variant={TextVariant.SECONDARY}>Copy the command below</Text>
          </StepRow>
          <StepRow>
            <StepNumber>
              <Text variant={TextVariant.TERTIARY}>2</Text>
            </StepNumber>
            <Text variant={TextVariant.SECONDARY}>Open a terminal on your machine</Text>
          </StepRow>
          <StepRow>
            <StepNumber>
              <Text variant={TextVariant.TERTIARY}>3</Text>
            </StepNumber>
            <Text variant={TextVariant.SECONDARY}>
              Paste and run — we&apos;ll confirm receipt automatically
            </Text>
          </StepRow>
        </FlexWrapper>
      </Widget>

      <Widget fillWidth noHover>
        <FlexWrapper direction={FlexDirection.COLUMN} gap={8} fillWidth>
          <Text variant={TextVariant.TERTIARY}>
            {signature ? "Ready to run" : "Generating command..."}
          </Text>
          <FlexWrapper alignItems={AlignItems.START} gap={8} fillWidth>
            <CodeEditor
              content={curlCommand}
              lang="bash"
              isReadOnly
              noLineNumbers
              borderRadius={8}
            />
            <Button
              icon={copied ? CheckIcon : CopyIcon}
              variant={ButtonVariant.SECONDARY}
              size={ButtonSize.MEDIUM}
              onClick={handleCopy}
            />
          </FlexWrapper>
        </FlexWrapper>
      </Widget>
    </FlexWrapper>
  );
};

export default WebhookQuickCurl;
